/**
 * Dashboard Order Details Modal Component
 * 
 * This component displays detailed information about a specific order
 * for the dashboard view (read-only, no approval actions).
 */

import React from 'react';
import { useTranslations } from '../../context/TranslationsContext';
import { useApp } from '../../context/AppContext';

const DashboardOrderDetailsModal = ({ order, onClose }) => {
  const { translations } = useTranslations();
  const { state } = useApp();
  const t = translations[state.currentLanguage];

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString(state.currentLanguage === 'ar' ? 'ar-SA' : 'en-US');
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat(state.currentLanguage === 'ar' ? 'ar-SA' : 'en-US').format(price);
  };

  const createGoogleMapsLink = (address) => {
    if (!address) return null;
    const encodedAddress = encodeURIComponent(address);
    return `https://www.google.com/maps/search/?api=1&query=${encodedAddress}`;
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-yellow-100 text-yellow-800',
      approved: 'bg-blue-100 text-blue-800',
      rejected: 'bg-red-100 text-red-800',
      active: 'bg-green-100 text-green-800',
      in_warehouse: 'bg-blue-100 text-blue-800',
      delivering_to_customer: 'bg-purple-100 text-purple-800',
      delivering_to_warehouse: 'bg-orange-100 text-orange-800',
      completed: 'bg-gray-100 text-gray-800',
      needs_attention: 'bg-red-100 text-red-800',
      retrieval_requested: 'bg-orange-100 text-orange-800',
      retrieval_approved: 'bg-green-100 text-green-800',
      retrieval_delivering: 'bg-purple-100 text-purple-800',
      retrieval_completed: 'bg-gray-100 text-gray-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  if (!order) return null;

  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <div className="modal-header">
          <h3>{t.orderDetails}</h3>
          <button onClick={onClose} className="close-btn">×</button>
        </div>
        
        <div className="modal-body">
          <div className="order-details-grid">
            <div className="detail-section">
              <h4>{t.orderInfo}</h4>
              <div className="detail-item">
                <span className="label">{t.orderNumber}:</span>
                <span className="value">#{order.id.substring(0, 8)}</span>
              </div>
              <div className="detail-item">
                <span className="label">{t.status}:</span>
                <span className="value">
                  <span className={`status-badge ${getStatusColor(order.status)}`}>
                    {t[order.status]}
                  </span>
                </span>
              </div>
              <div className="detail-item">
                <span className="label">{t.createdAt}:</span>
                <span className="value">{formatDate(order.created_at)}</span>
              </div>
              <div className="detail-item">
                <span className="label">{t.price}:</span>
                <span className="value">{formatPrice(order.total_price)} {t.sar}</span>
              </div>
              <div className="detail-item">
                <span className="label">{t.adminNotes}:</span>
                <span className="value">{order.admin_notes || t.noNotes}</span>
              </div>
            </div>

            <div className="detail-section">
              <h4>{t.customerInfo}</h4>
              <div className="detail-item">
                <span className="label">{t.customerName}:</span>
                <span className="value">{order.customer_name}</span>
              </div>
              <div className="detail-item">
                <span className="label">{t.phone}:</span>
                <span className="value">{order.customer_phone}</span>
              </div>
              <div className="detail-item">
                <span className="label">{t.address}:</span>
                <span className="value">{order.customer_address}</span>
              </div>
            </div>

            <div className="detail-section">
              <h4>{t.storageDetails}</h4>
              <div className="detail-item">
                <span className="label">{t.space}:</span>
                <span className="value">{order.space_required} {t.sqm}</span>
              </div>
              <div className="detail-item">
                <span className="label">{t.duration}:</span>
                <span className="value">{order.duration_months} {t.months}</span>
              </div>
              <div className="detail-item">
                <span className="label">{t.items}:</span>
                <span className="value">{order.items_description}</span>
              </div>
              {order.additional_services && order.additional_services.length > 0 && (
                <div className="detail-item">
                  <span className="label">{t.additionalServices}:</span>
                  <span className="value">{order.additional_services.join(', ')}</span>
                </div>
              )}
            </div>

            {order.images && order.images.length > 0 && (
              <div className="detail-section">
                <h4>{t.images}</h4>
                <div className="images-grid">
                  {order.images.map((image, index) => (
                    <img
                      key={index}
                      src={`data:image/jpeg;base64,${image}`}
                      alt={`Order image ${index + 1}`}
                      className="order-image"
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Retrieval specific information */}
            {order.status && order.status.startsWith('retrieval_') && (
              <div className="detail-section">
                <h4>{t.retrievalDetails}</h4>
                {order.retrieval_delivery_address && (
                  <div className="detail-item">
                    <span className="label">{t.deliveryAddress}:</span>
                    <span className="value">
                      <a 
                        href={createGoogleMapsLink(order.retrieval_delivery_address)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="address-link"
                        style={{
                          color: '#1976d2',
                          textDecoration: 'underline',
                          cursor: 'pointer'
                        }}
                      >
                        {order.retrieval_delivery_address} 📍
                      </a>
                    </span>
                  </div>
                )}
                {order.retrieval_delivery_date && (
                  <div className="detail-item">
                    <span className="label">{t.requestedDeliveryDate}:</span>
                    <span className="value">{formatDate(order.retrieval_delivery_date)}</span>
                  </div>
                )}
                {order.retrieval_requested_at && (
                  <div className="detail-item">
                    <span className="label">{t.retrievalRequestDate}:</span>
                    <span className="value">{formatDate(order.retrieval_requested_at)}</span>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
        
        <div className="modal-footer">
          <button onClick={onClose} className="btn-secondary">
            {t.close}
          </button>
        </div>
      </div>
    </div>
  );
};

export default DashboardOrderDetailsModal;
